// ADR 7.10.26v §3 — asking the facade without pulling it forward.
//
// The spread has no first-frame twin (7.10.26u §5), so the shell is `OnePage`
// until this resolves. It waits on `afterFirstPaint` (1.10.26p) for every
// visitor, then imports the same specifier `startWasm` does, so the module
// evaluates once: a hinted visitor's facade is usually already in flight, and an
// unhinted visitor's download is no earlier than `preloadHint.ts` allows.
//
// ADR 16.10.26p §3 — whether the init failed is the outcome's to say, awaited
// after the import resolved, never a slot read off the namespace Safari may
// return before the facade's body has run.
//
// Never a static import of `../../wasm` — this module is on the boot graph and
// `bootGraph.test.ts` fails one. Called from the reading hook's mount effect,
// never at module scope (2.10.26v).

import { afterFirstPaint } from '../../app/firstPaint'
import { wasmInitOutcome } from '../../wasm/initOutcome'
import type { SpreadFn } from './readSpread'

/** The slice of the facade this module reads. */
export interface SpreadFacade {
  spreadLayout: SpreadFn
}

/** The facade import: a seam so a suite can drive a rejection. */
export type FacadeImport = () => Promise<SpreadFacade>

const importFacade: FacadeImport = () => import('../../wasm')

/** Resolves to the facade's `spreadLayout`, or `null` after a failed init or import. Never rejects. */
export function loadSpreadFacade(
  load: FacadeImport = importFacade,
  outcome: Promise<unknown> = wasmInitOutcome,
): Promise<SpreadFn | null> {
  return new Promise<void>((resolve) => afterFirstPaint(window, resolve))
    .then(load)
    .then(async (facade) => ((await outcome) === null ? facade.spreadLayout : null))
    .catch(() => null)
}
